import { isObject } from 'es-toolkit/compat';
import { DateTime } from 'luxon';
import { computeRelative } from './computeRelative';
import { parse } from './parseDateTime';
import type { FormatProps } from './types';
import { YEAR } from './units';

const FALLBACK_PRESET = 'DATE_FULL';

export function format(
  value: DateTime,
  {
    format: dateFormat,
    locale,
    stripSameYear,
    recentLabel,
    relative
  }: FormatProps
) {
  const dateTime = parse(value);

  const { isRelative, relativeObserverTimeout } = computeRelative(
    dateTime,
    relative
  );

  if (isRelative) {
    const isRecent =
      Math.abs(DateTime.now().diff(dateTime, 'minutes').minutes) < 1;

    return {
      relativeObserverTimeout,
      formattedDate:
        isRecent && recentLabel
          ? recentLabel
          : dateTime.toRelative({
              locale: formatLocale(locale)
            })
    };
  }

  const formatObj = defineFormat(dateFormat, {
    stripYear: stripSameYear && isSameYear(dateTime)
  });

  return {
    relativeObserverTimeout,
    formattedDate: dateTime
      .toLocaleString(
        {
          ...formatObj
        },
        { locale: formatLocale(locale) }
      )
      .replaceAll(' ', '\u00A0')
  };
}

export function formatFull(
  value: DateTime,
  locale?: FormatProps['locale']
): string {
  const dateTime = parse(value);
  const formatObj = defineFormat('DATETIME_FULL');

  return dateTime.toLocaleString(formatObj, {
    locale: formatLocale(locale)
  });
}

function formatLocale(locale?: FormatProps['locale']): string | undefined {
  if (!locale || typeof locale !== 'string' || locale === 'auto') {
    return undefined;
  }

  return locale.replace('_', '-');
}

function isSameYear(dateTime: DateTime): boolean {
  return dateTime.get(YEAR) === DateTime.local().get(YEAR);
}

function defineFormat(
  dateFormat?: string | Intl.DateTimeFormatOptions,
  { stripYear = false } = {}
): Intl.DateTimeFormatOptions {
  let formatObj: Intl.DateTimeFormatOptions;

  if (isObject(dateFormat)) {
    formatObj = dateFormat as Intl.DateTimeFormatOptions;
  } else if (typeof dateFormat === 'string' && dateFormat in DateTime) {
    formatObj = { ...DateTime[dateFormat as keyof typeof DateTime] };
  } else {
    formatObj = { ...DateTime[FALLBACK_PRESET] };
  }

  return {
    ...formatObj,
    ...(stripYear ? { year: undefined } : {})
  };
}
